import {
  Button,
  Collection,
  Header,
  type ListBoxItemProps,
  ListBoxItem as RACListBoxItem,
  type SectionProps as RACSectionProps,
  Select as RACSelect,
  type SelectProps as RACSelectProps,
  Section,
  SelectValue,
  composeRenderProps,
} from 'react-aria-components';
import { twMerge } from 'tailwind-merge';
import { CheckIcon } from './icons';
import { ListBox, type ListBoxProps } from './list-box';
import { Popover, type PopoverProps } from './popover';
import { Small } from './text';
import {
  composeTailwindRenderProps,
  focusRingStyle,
  inputFieldStyle,
  selectBoxIndicator,
} from './utils';

export function Select<T extends object>(props: RACSelectProps<T>) {
  return (
    <RACSelect
      {...props}
      data-ui="select"
      className={composeTailwindRenderProps(props.className, [
        'w-full min-w-56',
        ...inputFieldStyle,
      ])}
    />
  );
}

export function SelectButton(props: {
  className?: string;
  plain?: boolean;
  children?: React.ReactNode;
}) {
  return (
    <Button
      data-ui="control"
      className={composeRenderProps(props.className, (className, { isFocusVisible }) =>
        twMerge(
          'relative flex w-full cursor-default items-center gap-x-1 rounded-sm outline-hidden transition',
          'pe-7 ps-2.5',
          'py-[calc(--spacing(2.5)-1px)]',
          ' sm:py-[calc(--spacing(1.5)-1px)]',
          'group-invalid:border-destructive',
          'group:disabled:cursor-not-allowed group-disabled:opacity-50',
          'text-base/6 sm:text-sm/6',
          '[&>*:has(+[data-ui=select-value])>svg]:size-4',
          '[&>*:has(+[data-ui=select-value])>svg]:text-muted',
          !props.plain && 'hover:bg-zinc-100 dark:hover:bg-zinc-800 border shadow-xs',
          isFocusVisible ? focusRingStyle : '',
          'ring-offset-0',
          selectBoxIndicator,
          className,
        ),
      )}
    >
      {!!props.children && <span className="flex items-center gap-x-2">{props.children}</span>}
      <SelectValue
        data-ui="select-value"
        className={twMerge([
          'flex-1 truncate  data-placeholder:text-start data-placeholder:text-muted dark:data-placeholder:text-white',
          // Selected Item style
          '*:data-[ui=item]:flex',
          '*:data-[ui=item]:items-center',
          '*:data-[ui=item]:gap-x-2',
          '[&>[data-ui=item]_[data-ui=description]]:sr-only',
          '[&>[data-ui=item]_[data-ui=icon]:not([class*=size-])]:size-5',
          '[&>[data-ui=item]_[role=img]]:size-6',
          'sm:[&>[data-ui=item]_[data-ui=icon]:not([class*=size-])]:size-4',
          'sm:[&>[data-ui=item]_[role=img]]:size-5',
        ])}
      />
    </Button>
  );
}

export function SelectPopover({ className, placement = 'bottom', ...props }: PopoverProps) {
  return (
    <Popover
      {...props}
      className={composeTailwindRenderProps(className, [
        'w-(--trigger-width)',
        !props.plain && 'dark:bg-zinc-800 dark:ring-zinc-700',
      ])}
      placement={placement}
    />
  );
}

export function SelectListBox<T extends object>(props: ListBoxProps<T>) {
  return (
    <ListBox
      {...props}
      className={composeTailwindRenderProps(props.className, [
        'flex max-h-[inherit] flex-col overflow-auto p-1 has-[header]:px-2 has-[header]:pt-0',

        // Listbox item
        '**:data-[ui=item]:grid',
        '**:data-[ui=item]:grid-cols-[minmax(16px,max-content)_1fr]',
        '**:data-[ui=item]:items-center',
        '**:data-[ui=item]:gap-x-2',

        // icon
        '[&_[data-ui=item]>[data-ui=icon]:has(+[data-ui=label]):not([class*=size-])]:size-4',

        // icon + label
        '[&_[data-ui=item]>[data-ui=label]]:col-span-full',
        // label
        '[&:has([data-ui=icon]+[data-ui=label])_[data-ui=item]>[data-ui=label]]:col-start-2',

        // icon + label + description
        // description
        '[&:has([data-ui=icon]+[data-ui=label]+[data-ui=description])_[data-ui=item]>[data-ui=description]]:col-start-2',

        // image
        '[&_[data-ui=item]>[role=img]:has(+[data-ui=label])]:size-5',

        // label
        '[&:has([role=img]+[data-ui=label])_[data-ui=item]>[data-ui=label]]:col-start-2',

        // Image + label + description
        // Image
        '[&_[data-ui=item]>[role=img]:has(+[data-ui=label]+[data-ui=description])]:size-7',
        '[&_[data-ui=item]>[role=img]:has(+[data-ui=label]+[data-ui=description])]:self-start',
        '[&_[data-ui=item]>[role=img]:has(+[data-ui=label]+[data-ui=description])]:mt-0.5',
        '[&_[data-ui=item]>[role=img]:has(+[data-ui=label]+[data-ui=description])]:row-start-1',
        '[&_[data-ui=item]>[role=img]:has(+[data-ui=label]+[data-ui=description])]:row-end-3',

        // label
        '[&:has([role=img]+[data-ui=label]+[data-ui=description])_[data-ui=item]>[data-ui=label]]:leading-5',

        // description
        '[&:has([role=img]+[data-ui=label]+[data-ui=description])_[data-ui=item]>[data-ui=description]]:col-row-2',
        '[&:has([role=img]+[data-ui=label]+[data-ui=description])_[data-ui=item]>[data-ui=description]]:col-start-2',
      ])}
    />
  );
}

export interface SectionProps<T> extends RACSectionProps<T> {
  title?: string;
}

export function SelectSection<T extends object>(props: SectionProps<T>) {
  return (
    <Section
      className={twMerge(
        'first:-mt-[1px]',
        'not-first:my-1.5',
        'not-first:border-t not-first:border-t-border/40',
        props.className,
      )}
    >
      <Header
        className={twMerge(
          'sticky z-10 truncate bg-white ps-8 pt-2 text-xs/4 text-muted dark:bg-zinc-800',
          'top-[0px] -mx-[1px] rounded-sm',
        )}
      >
        {props.title}
      </Header>
      <Collection items={props.items}>{props.children}</Collection>
    </Section>
  );
}

export function SelectListItem({
  destructive,
  plain,
  icon,
  ...props
}: ListBoxItemProps & { plain?: boolean; icon?: JSX.Element | false; destructive?: true }) {
  const textValue =
    props.textValue || (typeof props.children === 'string' ? props.children : undefined);

  return (
    <RACListBoxItem
      {...props}
      textValue={textValue}
      className={composeRenderProps(props.className, (className) => {
        return twMerge([
          'group flex cursor-default select-none items-center gap-x-2 rounded-sm outline-hidden',
          'px-1.5 py-2.5 has-submenu:pe-0 sm:py-1.5',
          'text-base/6 sm:text-sm/6',
          'disabled:opacity-50',
          !plain && 'focus:bg-zinc-100 dark:focus:bg-zinc-700',
          destructive && 'text-destructive',
          className,
        ]);
      })}
    >
      {composeRenderProps(props.children, (children) => {
        return (
          <>
            {icon !== undefined ? (
              icon
            ) : (
              <CheckIcon
                height={16}
                className={twMerge(
                  'mt-1 size-4 shrink-0 self-start in-data-[ui=select-value]:hidden',
                  'invisible group-selected:visible',
                )}
              />
            )}

            <div data-ui="item">{children}</div>
          </>
        );
      })}
    </RACListBoxItem>
  );
}

export function SelectListItemLabel({ className, ...props }: JSX.IntrinsicElements['span']) {
  return (
    <span slot="label" data-ui="label" className={twMerge('mb-0 truncate', className)} {...props} />
  );
}

export function SelectListItemDescription({ className, ...props }: JSX.IntrinsicElements['span']) {
  return <Small slot="description" data-ui="description" className={className} {...props} />;
}
